<template>
  <div v-if="partner" class="tw-m-2">
    <div class="tw-text-lg tw-font-bold">{{ partner.full_name }}</div>

    <div class="tw-w-[350px]">
      <FormField
        label="Exact Supplier Account"
        type="multi"
        v-model="form.exactAccountId"
        :options="exactAccountOptions"
        @search-change="exactAccountNameContains = $event"
      />
      <FormField
        label="Exact Warehouse"
        type="multi"
        v-model="form.exactWarehouseId"
        :options="exactWarehouseOptions"
        @search-change="exactWarehouseDescriptionContains = $event"
      />
      <FormField
        label="Schouw service"
        type="multi"
        v-model="form.inspectionService"
        :options="serviceOptions"
      />

      <button
        class="tw-mt-2 tw-p-2 tw-bg-green-500 hover:tw-bg-green-400 disabled:tw-bg-gray-400 tw-text-white tw-rounded-md"
        @click="save"
        :disabled="isLoading"
      >
        Opslaan
      </button>

      {{ error }}
    </div>


  </div>
</template>

<script setup>
import { useMutation, useQuery } from '@tanstack/vue-query'
import { useRoute } from 'vue-router/composables'
import { computed, reactive, ref, shallowRef, watch } from 'vue';
import FormField from '@/components/shared/FormField.vue';
import { refDebounced } from '@vueuse/core';
import { useRouter } from 'vue-router/composables';

const router = useRouter()
const route = useRoute()
const form = reactive({
  exactAccountId: null,
  exactWarehouseId: null,
  inspectionService: null,
})

const { data: partner } = useQuery({
  queryKey: computed(() => ['partners', route.params.id]),
  queryFn: async () => {
    const result = await fetch(`/tickets/api/v1/partners/${route.params.id}`)

    if (!result.ok) {
      throw new Error('Failed to fetch partner data')
    }

    const data = await result.json()
    return data.data
  },
  refetchOnWindowFocus: false,
  keepPreviousData: true,
})

watch(() => partner.value, () => {
  form.exactAccountId = partner.value?.exact_account_id || null
  form.exactWarehouseId = partner.value?.exact_warehouse_id || null
  form.inspectionService = partner.value?.inspection_service || null
})

const exactAccountNameContains = shallowRef('')
const exactAccountNameContainsDebounced = refDebounced(exactAccountNameContains, 500)
const { data: exactAccounts } = useQuery({
  queryKey: computed(() => ['exact-suppliers', exactAccountNameContainsDebounced.value, form.exactAccountId]),
  queryFn: async () => {
    const url = new URL('/tickets/api/v1/exact-suppliers', window.location.origin)

    if (exactAccountNameContainsDebounced.value) {
      url.searchParams.append('name_contains', exactAccountNameContainsDebounced.value)
    }
    else if (form.exactAccountId) {
      url.searchParams.append('id', form.exactAccountId)
    }

    const result = await fetch(url)

    if (!result.ok) {
      throw new Error('Failed to fetch exact suppliers')
    }

    const data = await result.json()
    return data.data
  },
  refetchOnWindowFocus: false,
  keepPreviousData: true,
  enabled: computed(() =>
    !!exactAccountNameContainsDebounced.value || !!form.exactAccountId
  ),
})

const exactAccountOptions = computed(() => exactAccounts.value?.map((a) => ({
  label: `${a.code} ${a.name}`,
  value: a.id,
})) || [])

const exactWarehouseDescriptionContains = shallowRef('')
const exactWarehouseDescriptionContainsDebounced = refDebounced(exactWarehouseDescriptionContains, 500)

const { data: exactWarehouses } = useQuery({
  queryKey: computed(() => ['exact-warehouses', exactWarehouseDescriptionContainsDebounced.value, form.exactWarehouseId]),
  queryFn: async () => {
    const url = new URL('/tickets/api/v1/exact-warehouses', window.location.origin)

    if (exactWarehouseDescriptionContainsDebounced.value) {
      url.searchParams.append('description_contains', exactWarehouseDescriptionContainsDebounced.value)
    }
    else if (form.exactWarehouseId) {
      url.searchParams.append('id', form.exactWarehouseId)
    }

    const result = await fetch(url)

    if (!result.ok) {
      throw new Error('Failed to fetch exact warehouses')
    }

    const data = await result.json()
    return data.data
  },
  refetchOnWindowFocus: false,
  keepPreviousData: true,
  enabled: computed(() =>
    !!exactWarehouseDescriptionContainsDebounced.value || !!form.exactWarehouseId
  ),
})

const exactWarehouseOptions = computed(() => exactWarehouses.value?.map((a) => ({
  label: `${a.code} ${a.description}`,
  value: a.id,
})) || [])

const { mutate: save, error, isLoading } = useMutation({
  mutationFn: async () => {
    const payload = {}
    if (form.exactAccountId) {
      payload.exact_account_id = form.exactAccountId
    }
    if (form.exactWarehouseId) {
      payload.exact_warehouse_id = form.exactWarehouseId
    }
    if (form.inspectionService) {
      payload.inspection_service = form.inspectionService
    }

    const result = await fetch(`/tickets/api/v1/partners/${partner.value.id}`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(payload),
    })

    if (!result.ok) {
      throw new Error('Failed to save partner data')
    }

    return result.json()
  },
  onSuccess: () => {
    router.push({ name: 'purchase-orders' })
  },
})

const { data: items } = useQuery({
  queryKey: computed(() => ['products', 'all']),
  queryFn: async () => {
    const url = new URL('/tickets/api/v1/products', window.location.origin)

    const result = await fetch(url)

    if (!result.ok) {
      throw new Error('Failed to fetch items')
    }

    const data = await result.json()
    return data.data
  },
  staleTime: Infinity,
  refetchOnWindowFocus: false,
  keepPreviousData: true,
})

const serviceOptions = computed(() => items.value?.map((a) => ({
  label: `${a.code} ${a.description}`,
  value: a.code,
})) || [])
</script>
